import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hand-rolled drag and drop with pointer events (works for mouse, pen, and touch, no library).
 * A press that moves less than a few pixels is a normal click. Drop targets are any element with
 * `data-drop-lane` and `data-drop-day`; the hook reports which one is under the pointer and the
 * pointer's y position inside it.
 */
export interface DragPayload {
  kind: "visit" | "job";
  id: string;
  label: string;
  /** The lane the card was picked up from (a crew visit shows in each of its people's lanes). */
  fromLane?: string;
}

export interface DropTarget {
  lane: string;
  day: string;
  /** Pointer y relative to the target's top, in pixels. */
  offsetY: number;
}

interface DragState {
  payload: DragPayload;
  x: number;
  y: number;
  target: DropTarget | null;
}

function targetAt(x: number, y: number): DropTarget | null {
  const el = document
    .elementFromPoint(x, y)
    ?.closest<HTMLElement>("[data-drop-lane][data-drop-day]");
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return { lane: el.dataset.dropLane!, day: el.dataset.dropDay!, offsetY: y - rect.top };
}

export function useDragDrop(onDrop: (payload: DragPayload, target: DropTarget) => void) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const pending = useRef<{ payload: DragPayload; x: number; y: number; pointerId: number } | null>(
    null,
  );
  const dropRef = useRef(onDrop);
  useEffect(() => {
    dropRef.current = onDrop;
  }, [onDrop]);
  const dragging = useRef(false);
  const justDragged = useRef(false);

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const p = pending.current;
      if (!p || e.pointerId !== p.pointerId) return;
      if (!dragging.current) {
        if (Math.hypot(e.clientX - p.x, e.clientY - p.y) < 6) return;
        dragging.current = true;
      }
      setDrag({
        payload: p.payload,
        x: e.clientX,
        y: e.clientY,
        target: targetAt(e.clientX, e.clientY),
      });
    };
    const up = (e: PointerEvent) => {
      const p = pending.current;
      if (!p || e.pointerId !== p.pointerId) return;
      pending.current = null;
      if (dragging.current) {
        const target = targetAt(e.clientX, e.clientY);
        if (target) dropRef.current(p.payload, target);
        // The click that follows pointerup must not open the job.
        justDragged.current = true;
        setTimeout(() => (justDragged.current = false), 0);
      }
      dragging.current = false;
      setDrag(null);
    };
    const cancel = () => {
      pending.current = null;
      dragging.current = false;
      setDrag(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
    };
  }, []);

  /** Spread onto a draggable element. */
  const handle = useCallback(
    (payload: DragPayload) => ({
      onPointerDown: (e: React.PointerEvent) => {
        if (e.button !== 0) return;
        pending.current = { payload, x: e.clientX, y: e.clientY, pointerId: e.pointerId };
      },
      // A click right after a drag should not open the job.
      onClickCapture: (e: React.MouseEvent) => {
        if (justDragged.current || dragging.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      // Links and images are natively draggable; the browser's own drag would cancel ours.
      draggable: false,
      onDragStart: (e: React.DragEvent) => e.preventDefault(),
      style: { touchAction: "none" as const, userSelect: "none" as const },
    }),
    [],
  );

  return { drag, handle };
}
